import type { RankingImportResult } from '@/api/category-ranking/types';
import { RANKING_IMPORT_ROW_STATUS } from '@/api/category-ranking/types';
import { formatDateForUser } from '@/utils/formatters';
import type React from 'react';

interface RankingImportSummaryProps {
  result: RankingImportResult;
  tournamentName: string;
  /** "YYYY-MM-DD" as picked in step 1. */
  earnedAt: string;
}

export const RankingImportSummary: React.FC<RankingImportSummaryProps> = ({
  result,
  tournamentName,
  earnedAt,
}) => {
  const errorRows = result.rows.filter((row) => row.error);
  const warningRows = result.rows.filter((row) => row.warning && !row.error);
  const newUserRows = result.rows.filter(
    (row) => row.status === RANKING_IMPORT_ROW_STATUS.NEW_USER,
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
        <p className="font-medium text-gray-800">{tournamentName}</p>
        <p className="text-xs text-gray-500">
          Fecha de los puntos: {formatDateForUser(`${earnedAt}T00:00:00`)}
        </p>
        <ul className="mt-3 space-y-1 text-gray-700">
          <li>
            <strong>{result.validRows}</strong> de {result.totalRows} filas
            válidas
          </li>
          <li>
            <strong>{result.usersToCreate.length}</strong> cuentas nuevas se van
            a crear
          </li>
          <li>
            <strong>{result.totalPoints}</strong> puntos en total
          </li>
        </ul>
        {result.usersToCreate.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-blue-600">
              Ver los {result.usersToCreate.length} emails sin cuenta
            </summary>
            <p className="mt-1 break-words text-xs text-gray-600">
              {result.usersToCreate.join(', ')}
            </p>
          </details>
        )}
        {newUserRows.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            A cada cuenta nueva le llega un mail de bienvenida y otro para
            definir su contraseña.
          </p>
        )}
      </div>

      {errorRows.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="mb-2 text-sm font-medium text-red-800">
            {errorRows.length} filas con error. No se va a importar nada hasta
            corregirlas.
          </p>
          <ul className="space-y-1 text-xs text-red-700">
            {errorRows.map((row) => (
              <li key={row.index}>
                Fila {row.index + 1}
                {row.email ? ` (${row.email})` : ''}: {row.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {warningRows.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="mb-2 text-sm font-medium text-amber-800">
            {warningRows.length} avisos
          </p>
          <ul className="space-y-1 text-xs text-amber-800">
            {warningRows.map((row) => (
              <li key={row.index}>
                Fila {row.index + 1}: {row.warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">#</th>
              <th className="px-3 py-2 text-left font-medium">Email</th>
              <th className="px-3 py-2 text-left font-medium">Cuenta</th>
              <th className="px-3 py-2 text-right font-medium">Puntos</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, index) => (
              <tr
                key={row.index}
                className={index > 0 ? 'border-t border-gray-100' : ''}
              >
                <td className="px-3 py-1.5 text-gray-500">{row.index + 1}</td>
                <td className="px-3 py-1.5">{row.email ?? '—'}</td>
                <td className="px-3 py-1.5 text-xs text-gray-600">
                  {row.error
                    ? '—'
                    : row.status === RANKING_IMPORT_ROW_STATUS.NEW_USER
                      ? 'Se crea'
                      : 'Existente'}
                </td>
                <td className="px-3 py-1.5 text-right font-medium">
                  {row.points ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
